import { Observable, Subject } from "rxjs";
import { ApiPage } from "../models/page.model";
import ApiRepo from "./api.repo";
import { inject } from "inversify";
import { bind } from "@react-rxjs/core";
import { ApiResponse, MultipleOpsResult } from "../models/response.model";
import dayjs, { Dayjs } from "dayjs";

export class TableRepo<T extends DatabaseRecord> {
  @inject(ApiRepo)
  protected apiRepo!: ApiRepo;

  //Notify data changes
  protected changes: Subject<number> = new Subject();
  public changes$: Observable<number> = this.changes.asObservable();
  public useChanges: () => number = bind(this.changes$, Date.now())[0];

  protected getPagePath: string = "page";
  protected getAllPath: string = "all";
  protected addPath: string = "add";
  protected updatePath: string = "update";
  protected deletePath: string = "delete";
  protected detailPath: string = "detail";

  constructor(protected basepath: string) {}

  protected normalizePayload(payload: Record<string, any>) {
    for (const property in payload) {
      const value = payload[property];
      if (value === "") {
        payload[property] = null;
        continue;
      }
      if (dayjs.isDayjs(value)) {
        payload[property] = (value as Dayjs).format();
      }
    }
    return payload;
  }

  async getData(
    index: number,
    params: object,
    pageSize = 10,
    customPath?: string
  ): Promise<ApiPage<T> | null> {
    const response = await this.apiRepo.get<ApiPage<T>>(
      `${this.basepath}/${customPath ?? this.getPagePath}`,
      {
        pageSize: pageSize,
        pageIndex: index,
        ...params,
      }
    );
    if (response?.result) {
      return response.result;
    }
    return null;
  }

  async getAll(params: object = {}, customPath?: string) {
    try {
      const response = await this.apiRepo.get<ApiPage<T>>(
        `${this.basepath}/${customPath ?? this.getAllPath}`,
        {
          ...params,
        }
      );
      if (response?.code === "00") {
        return response.result.data;
      }
    } catch (err) {
      console.log(err);
    }
    return [];
  }

  async create(model: T, path?: string): Promise<T | undefined> {
    try {
      console.log(path);
      //Clear model.id to prevent unexpected behaviors
      model.id = "";
      this.normalizePayload(model as Record<string, any>);
      const response = await this.apiRepo.post<T>(
        `${this.basepath}/${path ?? this.addPath}`,
        model
      );
      if (response?.code === "00") {
        this.changes.next(Date.now());
        return response.result ?? true;
      }
      return undefined;
    } catch (err) {
      console.log(err);
    }
  }
  async update(model: Partial<T>) {
    try {
      this.normalizePayload(model as Record<string, any>);
      const response = await this.apiRepo.post(
        `${this.basepath}/${this.updatePath}`,
        model
      );
      if (response?.code === "00") {
        this.changes.next(Date.now());
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
    }
  }

  async delete(id: string): Promise<ApiResponse<T> | undefined> {
    
      return await this.apiRepo.post(
        `${this.basepath}/${this.deletePath}`,
        { id: id }
      );
  }

  async deleteMany(models: T[]): Promise<MultipleOpsResult> {
    let success = 0;
    const responses = await Promise.all(
      models.map((model) =>
        this.apiRepo.post(`${this.basepath}/${this.deletePath}`, {
          id: model.id,
        })
      )
    );
    responses.forEach((response) => {
      if (response?.code === "00") {
        success++;
      }
    });
    if (success > 0) {
      this.changes.next(Date.now());
    }
    return { success, total: models.length };
  }

  async detail(id: string) {
    try {
      const response = await this.apiRepo.get<T>(
        `${this.basepath}/${this.detailPath}`,
        { id: id }
      );
      if (response?.code === "00") {
        return response.result;
      }
      return null;
    } catch (err) {
      console.log(err);
    }
  }

  async refresh() {
    this.changes.next(Date.now());
  }
}

export interface DatabaseRecord {
  id: string;
  key?: string;
}

export interface HasAddressRecord extends DatabaseRecord {
  provinceName?: string;
  districtName?: string;
  wardName?: string;
}
